import {forwardRef, useCallback, useImperativeHandle, useState} from 'react'

import {IDuplicateTableRef} from './ConnectedBarcodeDetails'
import { ISpecialProductModel } from '../core/_models'
import { UserModel } from '../../../auth'
import { useNavigate } from 'react-router-dom'
import Loader from '../../../common/Loader'
import AlertMessage from '../../../common/alert/AlertMsg'
import { applyConnectedProducts } from '../core/_requests'
import ConnectedBarcodeTableHead from './ConnectedBarcodeTableHead'
import ConnectedBarcodeTableRow from './ConnectedBarcodeTableRow'
import { useIntl } from 'react-intl'


interface IProps {
  products: ISpecialProductModel[],
  currentUser:UserModel | undefined
}
const ConnectedBarcodeTablee = forwardRef<IDuplicateTableRef, IProps>(({products,currentUser}, ref) => {
  const {formatMessage} = useIntl()
  let prevBarcode: string
  let barcodeState: string
  const navigate = useNavigate()

  const prepareState = () => {
    const tempState: any = {}
    for (let i = 0; i < products.length; i++) {
      const item = products[i]
      if (item.barcode !== barcodeState) {
        barcodeState = item.barcode
        tempState[item.barcode] = {
          error: true,
          keys: [],
        }
      }
    }
    return tempState
  }
  const [isError, setIsError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [state, setState] = useState<any>(prepareState);

  // Per-row editable quantity / sale price, keyed by supplier_product_special_id
  // and seeded from each product's current values.
  const [edits, setEdits] = useState<Record<number, {quantity: string | number; sale_price: string | number}>>(() => {
    const init: Record<number, {quantity: string | number; sale_price: string | number}> = {}
    products.forEach((p) => {
      init[p.supplier_product_special_id] = {
        quantity: p.data.quantity,
        sale_price: p.data.sale_price.new_sale_price,
      }
    })
    return init
  })

  const handleEdit = useCallback((id: number, field: 'quantity' | 'sale_price', value: string) => {
    setEdits((prev) => ({...prev, [id]: {...prev[id], [field]: value}}))
  }, [])

  const handleRowChange = useCallback((barcode: string, key: number, isChecked: boolean) => {
    const selectedState = state[barcode]
    if (isChecked) {
      selectedState.keys.push(key)
      selectedState.error = false
    } else {
      selectedState.keys = selectedState.keys.filter((k: number) => k !== key)
      selectedState.error = true
    }
    setState({...state, [barcode]: {...selectedState}})
  }, [])
  const submitTable = async () => {
    setIsError(false);
    let checkValidity = true;
    let selectedKeys:string[] = []
    for(let i in state) {
      if (state[i].error === true) {
        checkValidity = false;
        break;
      }
      selectedKeys.push(...state[i].keys)
    }
    if (checkValidity) {
      try {
        setIsLoading(true)
        const editsPayload: Record<string, {quantity: string | number; sale_price: string | number}> = {}
        selectedKeys.forEach((k) => {
          if (edits[k as any]) editsPayload[k] = edits[k as any]
        })
        const data = await applyConnectedProducts(currentUser?.token,{
          special_ids: selectedKeys,
          edits: editsPayload,
        })
        
        if (data) {
          // Selecting connected products + Done IS the final submit now —
          // it goes straight to the manager queue (APPLIED), so we land on
          // the confirmation, not the old "Send the request" pending page.
          navigate('/thank-you?go=request-add-discount')
        } else {
          setIsError(true);
        }
        setIsLoading(false)
      } catch {
        setIsError(true);
        setIsLoading(false)
      }
      
    } else {
      setIsError(true);
    }
  }
  useImperativeHandle(ref, () => ({submitTable}))

  return (
    <>
        {/* begin::Table */}
        <table className='table align-middle gs-0 gy-5'>
          <ConnectedBarcodeTableHead />

          {/* begin::Table body */}
          <tbody>
            {products &&
              products.map((item, index) => {
                const isNew = index !== 0 && prevBarcode !== item.barcode
                prevBarcode = item.barcode
                return (
                  <ConnectedBarcodeTableRow
                    isNewBarcode={isNew}
                    onChange={handleRowChange}
                    onEdit={handleEdit}
                    edit={edits[item.supplier_product_special_id]}
                    key={item.supplier_product_special_id}
                    item={item}
                  />
                )
              })}
          </tbody>
          {/* end::Table body */}
        </table>
        {/* end::Table */}
      {isLoading && <Loader isFull={true} />}
      {isError && <AlertMessage title={formatMessage({id: 'at_least_select_alert_title'})} message={formatMessage({id: 'at_least_select_alert_desc'})} onClose={()=> setIsError(false)} />}
    </>
  )
})

export default ConnectedBarcodeTablee
